/**
 * The required-setup GATE (user-setup design §5.1 — DS-2 uniform
 * enforcement): while `setup.status` reports outstanding required-blocking
 * steps, the app routes into the setup surface (/identity?onboarding=1) and
 * keeps it foregrounded — only setup-completing routes stay reachable
 * (US-INV-7: the gate contains its own exit). Evaluation is runtime-side;
 * this component never recomputes completion. Errors fail OPEN (US-INV-4):
 * no status → no gate. Re-checks on a short poll while gated (broadcast
 * delivery is unreliable — Part A §4 platform finding).
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { isSetupGateAllowedPath, shouldGateSetup } from "@/lib/contacts-logic";
import { useGatewayStore } from "@/stores/useGatewayStore";
import type { SetupStatus } from "@/types/contacts";

const GATED_POLL_MS = 20_000;

export function SetupGate() {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const checked = useRef(false);

  // One check per session at connect (the session-start definition, DS-6).
  useEffect(() => {
    if (checked.current || !rpc || connectionState !== "connected") return;
    checked.current = true;
    rpc
      .setupStatus()
      .then(setStatus)
      .catch(() => setStatus(null)); // fail OPEN — no status, no gate
  }, [rpc, connectionState]);

  const gated = status !== null && shouldGateSetup(status);

  // While gated: poll until the blocking set drains (fallback-first refresh).
  useEffect(() => {
    if (!gated || !rpc) return;
    const timer = setInterval(() => {
      rpc
        .setupStatus()
        .then(setStatus)
        .catch(() => undefined);
    }, GATED_POLL_MS);
    return () => clearInterval(timer);
  }, [gated, rpc]);

  // While gated: any navigation outside the allowlist routes back into the
  // setup surface.
  useEffect(() => {
    if (!gated) return;
    if (!isSetupGateAllowedPath(location.pathname)) {
      navigate("/identity?onboarding=1", { replace: true });
    }
  }, [gated, location.pathname, navigate]);

  if (!gated) return null;
  return (
    <div
      className="fixed inset-x-0 top-0 z-50 bg-amber-500/95 px-4 py-1.5 text-center text-xs font-medium text-white"
      data-testid="setup_gate_banner"
    >
      {t(
        "onboarding.gateBanner",
        "Quase lá! Seu assistente precisa de um passo rapidinho (menos de um minuto) para poder cuidar de tudo para você — é só completar o item destacado. 💛",
      )}
    </div>
  );
}
